import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, Label, Select, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { STAGES_MAX, moveItem, removedWithLeads, toDrafts, toStagesPut, validateStages, type StageDraft, type StageError } from "./lead-logic";
import type { Stage } from "./lead-types";
import { StageDraftRow } from "./stage-draft-row";
import { useSaveStages } from "./use-leads";

const CONFLICT = 409;

interface StageEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: readonly Stage[];
}

function useErrorText() {
  const { t } = useTranslation();
  return (error: StageError) =>
    error.code === "reassignRequired"
      ? t("engage.leads.stagesDialog.reassignRequired", { name: error.stage })
      : t(`engage.leads.stagesDialog.${error.code}`, { count: error.code === "tooMany" ? STAGES_MAX : 2 });
}

function EditorBody({ stages, onDone }: { stages: readonly Stage[]; onDone: () => void }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const errorText = useErrorText();
  const save = useSaveStages();
  const [original] = useState(() => toDrafts(stages));
  const [drafts, setDrafts] = useState<StageDraft[]>(original);
  const [reassign, setReassign] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [conflict, setConflict] = useState(false);
  const errors = validateStages(original, drafts, reassign);
  const removed = removedWithLeads(original, drafts);
  const targets = drafts.filter((d) => d.id !== null);

  const update = (index: number, next: StageDraft | null) => {
    setDrafts((current) => (next ? current.map((d, i) => (i === index ? next : d)) : current.filter((_, i) => i !== index)));
  };
  const onSave = async () => {
    setSubmitted(true);
    if (errors.length > 0) return;
    try {
      await save.mutateAsync(toStagesPut(drafts, reassign));
      toast.success(t("engage.leads.stagesDialog.saved"));
      onDone();
    } catch (caught) {
      if (isApiError(caught) && caught.status === CONFLICT) setConflict(true);
      else toast.error(errorMessage(caught));
    }
  };

  return (
    <div className="space-y-4">
      <ol className="space-y-2">
        {drafts.map((draft, index) => (
          <StageDraftRow key={draft.key} draft={draft} index={index} count={drafts.length} onChange={(next) => { update(index, next); }} onMove={(direction) => { setDrafts((current) => moveItem(current, index, direction)); }} />
        ))}
      </ol>
      <Button
        variant="secondary"
        size="sm"
        disabled={drafts.length >= STAGES_MAX}
        onClick={() => {
          setDrafts((current) => [...current, { key: `new-${String(Date.now())}`, id: null, name: t("engage.leads.stagesDialog.newStage"), outcome: "open", leadCount: 0 }]);
        }}
      >
        <Icon name="plus" />
        {t("engage.leads.stagesDialog.add")}
      </Button>
      {removed.map((stage) => (
        <div key={stage.key} className="rounded-md border border-warning bg-warning-soft p-3">
          <Label htmlFor={`reassign-${stage.key}`}>{t("engage.leads.stagesDialog.reassignLabel", { name: stage.name })}</Label>
          <Select
            id={`reassign-${stage.key}`}
            value={stage.id ? (reassign[stage.id] ?? "") : ""}
            onChange={(event) => {
              const id = stage.id;
              if (id) setReassign((current) => ({ ...current, [id]: event.target.value }));
            }}
          >
            <option value="">{t("engage.leads.stagesDialog.reassign")}</option>
            {targets.map((target) => (
              <option key={target.key} value={target.id ?? ""}>
                {target.name}
              </option>
            ))}
          </Select>
        </div>
      ))}
      {(submitted && errors.length > 0) || conflict ? (
        <ul role="alert" className="space-y-1 text-sm font-semibold text-danger">
          {conflict ? <li>{t("engage.leads.stagesDialog.conflict")}</li> : null}
          {submitted ? errors.map((error) => <li key={error.code + (error.code === "reassignRequired" ? error.stage : "")}>{errorText(error)}</li>) : null}
        </ul>
      ) : null}
      <div className="flex justify-end gap-2 border-t border-line pt-3">
        <Button variant="secondary" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button loading={save.isPending} onClick={() => void onSave()}>
          {t("engage.leads.stagesDialog.save")}
        </Button>
      </div>
    </div>
  );
}

/** Pipeline stage editor (`PUT /v1/lead-stages`), workspace.manage only (DEC-031). */
export function StageEditorDialog({ open, onOpenChange, stages }: StageEditorProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t("engage.leads.stagesDialog.title")} description={t("engage.leads.stagesDialog.description")} className="max-w-xl" closeLabel={t("common.close")}>
        {open ? (
          <EditorBody
            stages={stages}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
